'use client'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui'
import { ArrowUpDown } from 'lucide-react'

import { RENTAL_SORTS, type RentalSort } from '@/lib/rental'
import { SURFACE_SOLID } from '@/lib/surface'
import { META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The toolbar's ordering control.
 *
 * It had a neighbour until now — a length picker that repriced every card for
 * 3, 7, 14 or 30 days. That control is gone from the listing by request, so the
 * grid quotes the daily rate and the lengths are chosen on the item's own page,
 * where the calendar and the savings ladder already live. The line under the
 * toolbar still names them, so nothing about the offer goes unsaid here.
 *
 * Kept in its own file rather than folded back into the toolbar: dropdown
 * markup is the bulkiest thing in that shell, and the toolbar is already at the
 * size this repo holds a file to.
 */
export function RentalSortSelect({
  sort,
  onSort,
  total,
}: {
  sort: RentalSort
  onSort: (sort: RentalSort) => void
  /** Drives nothing but the disabled state. */
  total: number
}) {
  return (
    // No results, no ordering control — a sort over nothing is a control that
    // cannot do anything, offered beside an empty state that is already asking
    // the reader to change something else.
    <Select
      value={sort}
      onValueChange={(next) => onSort(next as RentalSort)}
      disabled={total === 0}
    >
      {/* Three things the trigger dictates, all learned the hard way: the icon
          and the value must be DIRECT children (a flex wrapper renders them
          stacked, because the trigger's `[&>span]:line-clamp-1` sets
          `display:-webkit-box` on it), the value's width has to be set from
          here (`SelectValue` is a Radix slot and drops a `className` of its
          own), and without the `flex-1` the chevron bunches up against the
          label instead of sitting at the right edge. */}
      <SelectTrigger
        aria-label="Sortowanie"
        className={cn(
          META,
          // White-based fill, matching the search field beside it: `white` is
          // theme-flipping ink here, `black` is not — `bg-black/40` rendered
          // this trigger as a dark slab on the light theme's near-white bar.
          'h-11 w-full min-w-0 justify-start rounded-full border-white/15 bg-white/[0.05] px-3 text-white sm:w-44 sm:px-3.5',
          'hover:border-white/25 focus:ring-white/25',
          '[&>span]:flex-1 [&>span]:truncate [&>span]:text-left',
        )}
      >
        <ArrowUpDown aria-hidden="true" className="shrink-0 text-white/55" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent className={cn('min-w-44 border-white/[0.12]', SURFACE_SOLID)}>
        {RENTAL_SORTS.map(({ key, label }) => (
          <SelectItem
            key={key}
            value={key}
            className={cn(META, 'text-white/75 focus:bg-white/10 focus:text-white')}
          >
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
